import { useEffect, useState } from 'react';
import { Receipt } from 'lucide-react';
import { costReceipts } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const KINDS = [['mtv', 'Music video'], ['episode', 'Episode'], ['film', 'Film']];
const PARTS = [['frames', 'Frames'], ['motion', 'Motion'], ['lipsync', 'Lip-sync'], ['voices', 'Voices'], ['ambience', 'Ambience']];
const usd = (v) => `$${Number(v || 0).toFixed(v > 0 && v < 0.1 ? 3 : 2)}`;
const mins = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.round((s || 0) % 60)).padStart(2, '0')}`;

function Card({ label, r, current, testId }) {
  return (
    <div className={cn('rounded-lg border p-3', current ? 'border-amber-400/60 bg-amber-500/5' : 'border-neutral-800 bg-neutral-950/60')} data-testid={testId}>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{label}</div>
      {r ? (
        <>
          <div className="truncate text-[11px] text-neutral-400">{r.title || 'Untitled'} · {mins(r.seconds)} min</div>
          <div className="mt-1 flex items-baseline gap-2"><span className="font-mono text-lg text-neutral-100">{usd(r.usd)}</span>
            <span className="font-mono text-[11px] text-amber-200">{usd(r.usd_per_minute)}/min</span></div>
          <div className="text-[10px] text-neutral-500">{r.credits} cr</div>
        </>
      ) : <div className="text-[11px] text-neutral-600">No finished run yet</div>}
    </div>
  );
}

/** True-cost receipt after a run, plus the latest receipt per kind side by side — the price to quote subscribers. */
export function CostReceipt({ production }) {
  const r = production?.cost?.receipt;
  const [all, setAll] = useState({});
  useEffect(() => { if (r) costReceipts().then((d) => setAll(d.receipts || {})).catch(() => {}); }, [r?.at]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return null;
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-neutral-800 bg-neutral-950/40 p-3" data-testid="cost-receipt">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex items-center gap-2 text-xs font-semibold text-neutral-100"><Receipt size={13} className="text-amber-300" /> True cost of this run</span>
        <span className="font-mono text-xs text-neutral-300" data-testid="cost-receipt-total">{usd(r.usd)} · {r.credits} cr · <span className="text-amber-200" data-testid="cost-receipt-per-minute">{usd(r.usd_per_minute)} per minute</span></span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-neutral-400" data-testid="cost-receipt-breakdown">
        {PARTS.map(([k, l]) => <span key={k}>{l} <span className={cn('font-mono', r.breakdown?.[k] ? 'text-neutral-200' : 'text-emerald-300')}>{r.breakdown?.[k] ? usd(r.breakdown[k]) : 'free'}</span></span>)}
        <span>Music, upscale & Final Cut <span className="font-mono text-emerald-300">free</span></span>
      </div>
      <div className="grid gap-2 sm:grid-cols-3" data-testid="cost-receipts-compare">
        {KINDS.map(([k, l]) => <Card key={k} label={l} r={all[k] || (r.kind === k ? r : null)} current={r.kind === k} testId={`cost-receipt-${k}`} />)}
      </div>
    </div>
  );
}
